import { parseTelegramStartParam, type StartParams } from '@core/links';

/**
 * Разбор Telegram start_param мини-аппа: слаг заведения, код приглашения, код подарка.
 * Формат один на оба входа и живёт в ядре (@core/links): там же собираются ссылки «поделиться», поэтому
 * сборка и разбор не разъезжаются.
 *
 * - "venueSlug" — только заведение;
 * - "venueSlug--ref<код>" — заведение + приглашение (код — referralCode профиля или legacy telegramId);
 * - "venueSlug--gift<код>" — заведение + подарок по ссылке;
 * - "venueSlug_ref<telegramId>" — старый формат приглашения;
 * - "<telegramId>" — совсем старый формат (только приглашение, заведение — из других источников).
 */
export type ParsedStartParam = StartParams;

export function parseStartParam(startParam: string | undefined): ParsedStartParam {
  return parseTelegramStartParam(startParam);
}

// Cache parsed result (start_param won't change during session)
// IMPORTANT: Only cache if we got a valid result to avoid race condition
// where cache is set before Telegram SDK is initialized
let cachedResult: ParsedStartParam | null = null;

export function getStartParamCached(): ParsedStartParam {
  // If we have a cached result with actual data, return it
  if (cachedResult !== null && (cachedResult.venueSlug || cachedResult.referralCode || cachedResult.giftClaimCode)) {
    return cachedResult;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initData = (window as any).Telegram?.WebApp?.initDataUnsafe;
    const tgStartParam = initData?.start_param as string | undefined;
    const result = parseStartParam(tgStartParam);

    // Only cache if we got valid data (avoid caching empty result before SDK is ready)
    if (result.venueSlug || result.referralCode || result.giftClaimCode) {
      console.log('[parseStartParam] Caching valid result:', result);
      cachedResult = result;
    } else {
      console.log('[parseStartParam] Not caching empty result (SDK may not be ready)');
    }

    return result;
  } catch (e) {
    console.warn('[parseStartParam] Failed to get Telegram start_param:', e);
    return { venueSlug: null, referralCode: null, giftClaimCode: null };
  }
}

// Get just the referral code (for convenience)
export function getReferralCode(): string | null {
  return getStartParamCached().referralCode;
}
